import React, { useState } from "react";
import {
  WorkflowRunStepOperation,
  AgentWorkflowRunStepOperationType,
  ToolCallSentOperationParameters,
  WorkflowRunTask,
  WorkflowRunTaskAssignee,
} from "@/store/workflowRunStore";
import { AgentWorkflowRunStepOperationStatus } from "@/store/workflowRunStore";
import {
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ChevronUp,
  Users,
  Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAgentWorkflowActionsStore } from "@/store/agentWorkflowActionsStore";
import { formatRelative } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveTask } from "@/lib/auth-utils";
import { Permission } from "@forgepoint/shared";
import { formatJson } from "@/modules/agents/components/helpers/jsonUtils";

interface OperationDisplayProps {
  operation: WorkflowRunStepOperation;
  task?: WorkflowRunTask;
  onApprove?: (taskId: string) => void;
  onDeny?: (taskId: string) => void;
  isApproving?: boolean;
  isDenying?: boolean;
}

export const OperationDisplay: React.FC<OperationDisplayProps> = ({
  operation,
  task,
  onApprove,
  onDeny,
  isApproving,
  isDenying,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getActionById } = useAgentWorkflowActionsStore();
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const canApprove = canUserApproveTask(user, task, canApproveAllTasks);

  // Only handle TOOL_CALL operations
  if (
    operation.operationParameters.type !==
    AgentWorkflowRunStepOperationType.TOOL_CALL
  ) {
    return null;
  }

  const params =
    operation.operationParameters as ToolCallSentOperationParameters;
  const actionId = operation.operationParameters.actionId;
  const action = actionId ? getActionById(actionId) : undefined;

  // Determine the status icon and color
  const getStatusDisplay = () => {
    switch (operation.status) {
      case AgentWorkflowRunStepOperationStatus.RUNNING:
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
          className: "text-blue-500",
          label: "",
        };
      case AgentWorkflowRunStepOperationStatus.COMPLETED:
        return {
          icon: <CheckCircle2 className="h-4 w-4" />,
          className: "text-green-500",
          label: "Completed " + formatRelative(operation.updatedAt, new Date()),
        };
      case AgentWorkflowRunStepOperationStatus.FAILED:
        return {
          icon: <XCircle className="h-4 w-4" />,
          className: "text-red-500",
          label: "Failed " + formatRelative(operation.updatedAt, new Date()),
        };
      case AgentWorkflowRunStepOperationStatus.REQUIRES_REVIEW:
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          className: "text-yellow-500",
          label:
            "Pending since " + formatRelative(operation.updatedAt, new Date()),
        };
      case AgentWorkflowRunStepOperationStatus.WAITING:
        return {
          icon: <Timer className="h-4 w-4" />,
          className: "text-orange-500",
          label:
            "Waiting since " + formatRelative(operation.updatedAt, new Date()),
        };
      case AgentWorkflowRunStepOperationStatus.CANCELLED:
        return {
          icon: <XCircle className="h-4 w-4" />,
          className: "text-gray-500",
          label: "Cancelled " + formatRelative(operation.updatedAt, new Date()),
        };
      default:
        return {
          icon: <Clock className="h-4 w-4" />,
          className: "text-gray-400",
          label: "",
        };
    }
  };

  const statusDisplay = getStatusDisplay();
  const actionName = action?.name || "Unknown Action";

  const formatAssigneeName = (assignee: WorkflowRunTaskAssignee): string => {
    const { firstName, lastName, email } = assignee;
    const name = [firstName, lastName].filter(Boolean).join(" ");
    if (name && email) {
      return `${name} (${email})`;
    }
    return name || email || "Unknown";
  };

  const assigneeNames =
    task?.assignees?.map(formatAssigneeName).filter(Boolean) ?? [];

  const formattedInput = formatJson(params.input);
  const formattedOutput = formatJson(params.output);

  return (
    <div className="border rounded-lg p-3 space-y-2 group transition-all duration-200 ease-in-out w-full min-w-0">
      <div className="flex flex-col gap-2">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <span className={cn("flex items-center gap-2")}>
              <span className={statusDisplay.className}>
                {statusDisplay.icon}
              </span>
              <span className="text-sm font-medium">{actionName}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground transition-opacity opacity-0 group-hover:opacity-100">
              {statusDisplay.label}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </div>

        {/* Show assignees and review buttons outside of expanded area if needed */}
        {operation.status ===
          AgentWorkflowRunStepOperationStatus.REQUIRES_REVIEW &&
          task &&
          task.status === "PENDING_REVIEW" &&
          !isExpanded && (
            <div className="flex flex-col gap-2 pl-6">
              {assigneeNames.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span>Assigned to {assigneeNames.join(", ")}</span>
                </div>
              )}
              {canApprove && onApprove && onDeny && (
                <div className="flex gap-2">
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => onApprove(task.id)}
                    disabled={isApproving || isDenying}
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approve
                      </>
                    ) : (
                      <>Approve</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onDeny(task.id)}
                    disabled={isApproving || isDenying}
                  >
                    {isDenying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cancel
                      </>
                    ) : (
                      <>Cancel</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
      </div>

      <div
        className={`mt-3 space-y-3 ${isExpanded ? "max-h-[600px]" : "max-h-0"} transition-all duration-200 ease-in-out w-full min-w-0 overflow-hidden`}
      >
        <ScrollArea className="max-h-[600px] overflow-y-auto w-full min-w-0">
          {/* Show input parameters */}
          {formattedInput && (
            <div className="space-y-2 w-full min-w-0">
              <div className="text-xs bg-muted p-2 rounded max-w-full w-full min-w-0 whitespace-pre-wrap break-words break-all font-mono">
                {JSON.stringify(formattedInput, null, 2)}
              </div>
            </div>
          )}

          {/* Show output/error */}
          {params.error && (
            <div className="space-y-2 w-full min-w-0">
              <h4 className="text-sm font-medium text-red-500">Error:</h4>
              <div className="text-xs bg-red-50 text-red-900 p-2 rounded max-w-full w-full min-w-0 whitespace-pre-wrap break-words break-all">
                {params.error}
              </div>
            </div>
          )}

          {formattedOutput && (
            <div className="space-y-2 w-full min-w-0">
              <h4 className="text-sm font-medium">Output:</h4>
              <div className="text-xs bg-muted p-2 rounded overflow-y-auto max-h-48 max-w-full w-full min-w-0 whitespace-pre-wrap break-words break-all font-mono">
                {JSON.stringify(formattedOutput, null, 2)}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};
